// useState that remembers its last value for the signed-in account
// (lib/pageCache): a tab opened a second time paints what it showed last
// time on the very first frame, then its normal fetch overwrites it quietly.
//
// Returns [value, set, fromCache]. fromCache = this mount started from a
// remembered value (false on the first-ever open, when the page shows its
// skeleton). Nobody signed in → a plain useState that remembers nothing.
// Display only — see the money note in lib/pageCache.
import { useCallback, useRef, useState, type SetStateAction } from "react";
import { pageCache } from "../lib/pageCache";

/** `part:<account id>` for the signed-in account, or null when signed out. */
export function accountKey(part: string): string | null {
  const id = pageCache.owner();
  return id ? `${part}:${id}` : null;
}

export function useAccountCache<T>(part: string, fallback: T) {
  const [key] = useState(() => accountKey(part));
  const [fromCache] = useState(() => !!key && pageCache.has(key));
  const [value, setValue] = useState<T>(() => (key && pageCache.has(key) ? (pageCache.get<T>(key) as T) : fallback));
  const keyRef = useRef(key);
  const set = useCallback((action: SetStateAction<T>) => {
    setValue((prev) => {
      const next = typeof action === "function" ? (action as (p: T) => T)(prev) : action;
      // Still the same account (a reply landing after a sign-out writes nothing).
      if (keyRef.current && keyRef.current === accountKey(part)) pageCache.set(keyRef.current, next);
      return next;
    });
  }, [part]);
  return [value, set, fromCache] as const;
}
